import { QueryClient } from '@tanstack/react-query';
import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import z from 'zod';

import {
  ApiError,
  createEnsureApiQueryData,
  getApi,
  mapCatalogDatacenter,
  mapOrganization,
  mapUser,
  useOrganizationQuery,
  useUserQuery,
} from 'src/api';
import { getCurrentProjectId, setCurrentProjectId } from 'src/api/hooks/project';
import { AuthKit } from 'src/application/authkit';
import { getConfig } from 'src/application/config';
import { identifyUserInIntercom } from 'src/application/intercom';
import { useOnboardingStep } from 'src/application/onboarding';
import { identifyUserInSentry, reportError } from 'src/application/sentry';
import { getUrlLatency } from 'src/application/url-latency';
import { MainLayout } from 'src/layouts/main/main-layout';
import { AccountLocked } from 'src/modules/account/account-locked';
import { TrialEnded } from 'src/modules/trial/trial-ended/trial-ended';
import { useTrial } from 'src/modules/trial/use-trial';
import { OnboardingPage } from 'src/pages/onboarding/onboarding.page';

export const Route = createFileRoute('/_main')({
  component: Component,

  validateSearch: z.object({
    'organization-id': z.string().optional(),
    settings: z.literal('true').optional(),
  }),

  async beforeLoad({ search, context: { authKit, queryClient, posthog } }) {
    const api = getApi(authKit.getAccessToken);
    const ensureApiQueryData = createEnsureApiQueryData(queryClient);

    if (search['organization-id']) {
      await switchOrganization(authKit, search['organization-id']);
    }

    const [user, organization] = await Promise.all([
      ensureApiQueryData('get /v1/account/profile', {}).then(({ user }) => mapUser(user!)),
      ensureApiQueryData('get /v1/account/organization', {})
        .then(({ organization }) => mapOrganization(organization!))
        .catch(() => undefined),
    ]);

    let projectId = getCurrentProjectId() ?? undefined;

    if (organization !== undefined) {
      // The current project id is persisted across reloads and organization
      // switches (including silent ones done through the WorkOS widgets). Make
      // sure it still points to a project that belongs to the active
      // organization, otherwise reset it to the organization's default project.
      // Without this, a stale id would be sent as the x-koyeb-project-id header
      // on every request and rejected by the API.
      if (
        projectId === undefined ||
        !(await projectBelongsToOrganization(ensureApiQueryData, projectId, organization.id))
      ) {
        projectId = organization.defaultProjectId;
        setCurrentProjectId(projectId);
      }

      await ensureApiQueryData('get /v1/projects/{id}', { path: { id: projectId } });
    }

    void identifyUserInIntercom(api, user);
    identifyUserInSentry(user);
    posthog?.identify(user.id);

    if (organization) {
      posthog?.group('segment_group', organization.id);
    }

    return {
      user,
      organization,
      projectId,
      posthog,
    };
  },

  async loader({ context: { queryClient, organization, seon } }) {
    const ensureApiQueryData = createEnsureApiQueryData(queryClient);

    seon.initialize(queryClient).catch(reportError);
    void preloadDatacentersLatencies(queryClient);

    const promises = new Set<Promise<unknown>>();

    promises.add(ensureApiQueryData('get /v1/catalog/regions', { query: { limit: '100' } }));
    promises.add(ensureApiQueryData('get /v1/catalog/instances', { query: { limit: '100' } }));

    if (organization && organization.status === 'ACTIVE') {
      promises.add(
        ensureApiQueryData('get /v1/organizations/{organization_id}/summary', {
          path: { organization_id: organization.id },
        }),
      );

      promises.add(
        ensureApiQueryData('get /v1/organizations/{organization_id}/quotas', {
          path: { organization_id: organization.id },
        }),
      );

      if (organization.latestSubscriptionId) {
        promises.add(
          ensureApiQueryData('get /v1/subscriptions/{id}', {
            path: { id: organization.latestSubscriptionId },
          }),
        );
      }

      promises.add(
        ensureApiQueryData('get /v1/account/organizations', {
          query: {
            limit: '10',
            statuses: ['ACTIVE', 'WARNING', 'LOCKED', 'DEACTIVATING', 'DEACTIVATED'],
          },
        }),
      );

      promises.add(
        ensureApiQueryData('get /v1/organization_members', {
          query: { limit: '10' },
        }),
      );

      promises.add(
        ensureApiQueryData('get /v1/projects', {
          query: { limit: '10' },
        }),
      );
    }

    await Promise.all(promises);
  },
});

async function projectBelongsToOrganization(
  ensureApiQueryData: ReturnType<typeof createEnsureApiQueryData>,
  projectId: string,
  organizationId: string,
) {
  try {
    const { project } = await ensureApiQueryData('get /v1/projects/{id}', { path: { id: projectId } });
    return project?.organization_id === organizationId;
  } catch (error) {
    // 404: the project no longer exists. 401: the stored project belongs to
    // another organization and is rejected. In both cases the id is stale and
    // must be replaced by the organization's default project.
    if (ApiError.is(error, 404) || ApiError.is(error, 401)) {
      return false;
    }

    throw error;
  }
}

async function switchOrganization(authKit: AuthKit, externalId: string) {
  await authKit.switchToOrganization({ organizationId: externalId });

  throw redirect({
    search: (prev) => ({ ...prev, 'organization-id': undefined }),
  });
}

async function preloadDatacentersLatencies(queryClient: QueryClient) {
  const ensureApiQueryData = createEnsureApiQueryData(queryClient);

  const datacenters = await ensureApiQueryData('get /v1/catalog/datacenters', {}).then((result) =>
    result.datacenters!.map(mapCatalogDatacenter),
  );

  if (getConfig('environment') !== 'production') {
    return;
  }

  await Promise.all(
    datacenters.map(async ({ id, domain }) => {
      if (id.includes('aws')) {
        return;
      }

      const url = `https://${domain}/health`;

      await queryClient.ensureQueryData({
        queryKey: ['datacenterLatency', url],
        queryFn: () => getUrlLatency(url),
      });
    }),
  );
}

function Component() {
  const userQuery = useUserQuery();
  const organizationQuery = useOrganizationQuery();

  const trial = useTrial();
  const onboardingStep = useOnboardingStep();

  const locked = [
    ApiError.isAccountLockedError(userQuery.error),
    ApiError.isAccountLockedError(organizationQuery.error),
    organizationQuery.data?.statusMessage === 'VERIFICATION_FAILED',
  ].some(Boolean);

  if (locked) {
    return <AccountLocked />;
  }

  if (trial?.ended) {
    return <TrialEnded />;
  }

  if (onboardingStep) {
    return <OnboardingPage step={onboardingStep} />;
  }

  return (
    <MainLayout>
      <Outlet />
    </MainLayout>
  );
}
